import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { Logo } from "../components/Logo";
import { ShieldCheck } from "lucide-react";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (element: HTMLElement | null, options: object) => void;
        };
      };
    };
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

export function AdminLoginPage() {
  const navigate = useNavigate();

  const [step, setStep]           = useState<"credentials" | "totp">("credentials");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [totpCode, setTotpCode]   = useState("");
  const [loading, setLoading]     = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const googleButtonRef           = useRef<HTMLDivElement>(null);
  const totpInputRef              = useRef<HTMLInputElement>(null);

  const handleSuccess = () => navigate("/admin");

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === "your_google_client_id_here") return;

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          setGoogleLoading(true);
          setError(null);
          try {
            const res = await fetch("/api/auth/google", {
              method:      "POST",
              credentials: "include",
              headers:     { "Content-Type": "application/json" },
              body:        JSON.stringify({ token: response.credential }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
              setError(data.message || "Google login failed.");
              return;
            }
            if (data.requires2FA) { setStep("totp"); setTimeout(() => totpInputRef.current?.focus(), 50); return; }
            handleSuccess();
          } catch {
            setError("Unable to connect to the server. Please try again.");
          } finally {
            setGoogleLoading(false);
          }
        },
      });

      window.google?.accounts.id.renderButton(googleButtonRef.current, {
        theme: "filled_black",
        size: "large",
        width: googleButtonRef.current?.offsetWidth ?? 320,
        text: "signin_with",
        shape: "rectangular",
      });
    };

    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/auth/login", {
        method:      "POST",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body:        JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || "Invalid credentials.");
        return;
      }

      if (data.requires2FA) {
        setStep("totp");
        setTimeout(() => totpInputRef.current?.focus(), 50);
        return;
      }

      handleSuccess();
    } catch {
      setError("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (totpCode.length !== 6) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/auth/2fa/confirm", {
        method:      "POST",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body:        JSON.stringify({ token: totpCode }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || "Invalid code.");
        setTotpCode("");
        return;
      }

      handleSuccess();
    } catch {
      setError("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const showGoogleButton = GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== "your_google_client_id_here";

  return (
    <div className="min-h-screen bg-[#101828] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <Logo className="w-20" white />
        </div>

        <div className="bg-[#1E2939] border border-white/10 rounded-lg p-8">

          {/* ── Credentials step ── */}
          {step === "credentials" && (
            <>
              <h1 className="text-white text-lg font-semibold mb-1">Admin Access</h1>
              <p className="text-white/50 text-sm mb-6">Sign in to the LEO Group admin dashboard.</p>

              {showGoogleButton && (
                <>
                  <div
                    ref={googleButtonRef}
                    className="w-full mb-4"
                    style={{ minHeight: 44 }}
                    aria-label="Sign in with Google"
                  />
                  {googleLoading && (
                    <p className="text-center text-white/40 text-xs mb-4">Verifying…</p>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-white/30 text-xs">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                </>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    placeholder="admin@leo.com"
                    autoComplete="username"
                    required
                    className="w-full px-4 py-3 rounded bg-[#101828] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1A1A]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    className="w-full px-4 py-3 rounded bg-[#101828] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1A1A]/50 transition-colors"
                  />
                </div>
                {error && <p className="text-xs text-red-400">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || googleLoading || !email || !password}
                  className="w-full bg-[#7B1A1A] hover:bg-[#6b1515] disabled:opacity-40 text-white py-3 rounded text-sm font-medium transition-colors"
                >
                  {loading ? "Signing in…" : "Sign In"}
                </button>
              </form>
            </>
          )}

          {/* ── TOTP step ── */}
          {step === "totp" && (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-[#7B1A1A]/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4.5 h-4.5 text-[#7B1A1A]" />
                </div>
                <div>
                  <h1 className="text-white text-base font-semibold">Two-Factor Authentication</h1>
                  <p className="text-white/40 text-xs">Enter the code from your authenticator app.</p>
                </div>
              </div>

              <form onSubmit={handleTotpSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wide">
                    Authenticator Code
                  </label>
                  <input
                    ref={totpInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => { setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    className="w-full px-4 py-3 rounded bg-[#101828] border border-white/10 text-white text-center text-xl font-mono tracking-[0.4em] placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-[#7B1A1A]/50 transition-colors"
                  />
                </div>
                {error && <p className="text-xs text-red-400">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  className="w-full bg-[#7B1A1A] hover:bg-[#6b1515] disabled:opacity-40 text-white py-3 rounded text-sm font-medium transition-colors"
                >
                  {loading ? "Verifying…" : "Verify Code"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setError(null); setTotpCode(""); }}
                  className="w-full text-white/30 hover:text-white/60 text-xs py-1 transition-colors"
                >
                  ← Back to sign in
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-white/20 text-xs mt-6">
          LEO Group · Restricted Access
        </p>
      </div>
    </div>
  );
}
